import { useEffect, useState, type ReactNode } from 'react';
import { useRouterState } from '@tanstack/react-router';
import { AsideHeader, FooterItem, MobileHeader, type AsideHeaderItem } from '@gravity-ui/navigation';
import type { MobileMenuItem } from '@gravity-ui/navigation';
import { Button, Icon, Text, type IconData } from '@gravity-ui/uikit';
import { ArrowRightFromSquare, ChartColumn, FolderHouse, Link as LinkIcon, Person } from '@gravity-ui/icons';
import type { ManagerMeResponse } from '@odonix/contracts';
import { useSessionStore } from '../lib/session/context.js';
import { isPlainClick, useGo } from '../ui/router-link.js';

/**
 * Кабинет менеджера — та же панель, что у админки: обзор, свои клиники,
 * реферальные ссылки. На телефоне разделы в меню, потому что менеджер
 * звонит с него.
 */

const SECTIONS: { id: string; to: string; title: string; icon: IconData; matches: (path: string) => boolean }[] = [
  { id: 'home', to: '/', title: 'Мои показатели', icon: ChartColumn, matches: (path) => path === '/' },
  { id: 'tenants', to: '/tenants', title: 'Мои клиники', icon: FolderHouse, matches: (path) => path.startsWith('/tenants') },
  { id: 'links', to: '/links', title: 'Мои ссылки', icon: LinkIcon, matches: (path) => path.startsWith('/links') },
];

const COMPACT_KEY = 'odonix.manager.aside.compact';

function usePhone(): boolean {
  const [phone, setPhone] = useState(() => window.matchMedia('(max-width: 767px)').matches);
  useEffect(() => {
    const list = window.matchMedia('(max-width: 767px)');
    const update = () => {
      setPhone(list.matches);
    };
    list.addEventListener('change', update);
    return () => {
      list.removeEventListener('change', update);
    };
  }, []);
  return phone;
}

export function AppShell({ manager, children }: { manager: ManagerMeResponse; children: ReactNode }) {
  const store = useSessionStore();
  const go = useGo();
  const path = useRouterState({ select: (state) => state.location.pathname });
  const phone = usePhone();
  const [compact, setCompact] = useState(() => window.localStorage.getItem(COMPACT_KEY) === '1');

  const logo = {
    text: 'Odonix Менеджер',
    iconSrc: '/icons/icon-64.png',
    iconSize: 28,
    href: '/',
    'aria-label': 'Odonix Менеджер, к показателям',
    onClick: (event: React.MouseEvent<HTMLElement>) => {
      if (!isPlainClick(event)) return;
      event.preventDefault();
      go('/');
    },
  };
  const content = () => <main className="min-h-full bg-[var(--g-color-base-background)]">{children}</main>;

  if (phone) {
    const items: MobileMenuItem[] = SECTIONS.map((section) => ({
      id: section.id,
      title: section.title,
      icon: section.icon,
      current: section.matches(path),
      onItemClick: () => {
        go(section.to);
      },
    }));
    return (
      <MobileHeader
        logo={logo}
        burgerMenu={{
          items,
          renderFooter: () => (
            <div className="flex flex-col items-start gap-2 p-4">
              <Text color="secondary">{manager.fullName}</Text>
              <Button view="outlined" onClick={() => void store.logout()}>
                <Icon data={ArrowRightFromSquare} size={16} />
                Выйти
              </Button>
            </div>
          ),
        }}
        burgerOpenTitle="Разделы"
        burgerCloseTitle="Закрыть"
        renderContent={content}
      />
    );
  }

  const menuItems: AsideHeaderItem[] = SECTIONS.map((section) => ({
    id: section.id,
    title: section.title,
    icon: section.icon,
    href: section.to,
    current: section.matches(path),
    onItemClick: (_item, _collapsed, event) => {
      if (!isPlainClick(event)) return;
      event.preventDefault();
      go(section.to);
    },
  }));

  return (
    <AsideHeader
      logo={logo}
      compact={compact}
      onChangeCompact={(value) => {
        setCompact(value);
        window.localStorage.setItem(COMPACT_KEY, value ? '1' : '0');
      }}
      headerDecoration
      menuItems={menuItems}
      collapseTitle="Свернуть панель"
      expandTitle="Развернуть панель"
      renderContent={content}
      renderFooter={() => (
        <>
          <FooterItem id="who" icon={Person} title={manager.fullName} tooltipText={manager.email} />
          <FooterItem id="logout" icon={ArrowRightFromSquare} title="Выйти" onItemClick={() => void store.logout()} />
        </>
      )}
    />
  );
}
